/**
 * Endpoints para gestión de recetas y sistema nutricional usando localStorage
 * Modo demo sin backend
 */
import { storage, STORAGE_KEYS, delay } from "../config";

// Generar ID único
const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

/**
 * Calcular valores nutricionales de una receta
 * Simula el cálculo basado en ingredientes
 */
const calculateNutritionFromIngredients = (recipe) => {
  const ingredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []).filter(
    (ing) => ing.recipe === recipe.id
  );
  
  const products = storage.get(STORAGE_KEYS.PRODUCTS, []);
  const totalYieldGrams = recipe.total_yield_grams || 100;
  
  let totalEnergy = 0;
  let totalProteins = 0;
  let totalFats = 0;
  let totalSaturatedFats = 0;
  let totalMonounsaturatedFats = 0;
  let totalPolyunsaturatedFats = 0;
  let totalCholesterol = 0;
  let totalCarbohydrates = 0;
  let totalSugars = 0;
  let totalSodium = 0;
  let totalFiber = 0;
  
  ingredients.forEach((ingredient) => {
    const product = products.find((p) => p.id === ingredient.ingredient);
    if (!product || !product.is_nutritional_ingredient) return;
    
    // Convertir cantidad a gramos (simplificado)
    let quantityGrams = ingredient.quantity || 0;
    if (ingredient.unit === "kg") quantityGrams *= 1000;
    if (ingredient.unit === "l") quantityGrams *= 1000; // Asumir densidad 1g/ml
    if (ingredient.unit === "ml") quantityGrams *= 1; // Asumir densidad 1g/ml
    
    // Calcular proporción
    const proportion = quantityGrams / totalYieldGrams;
    
    // Acumular valores nutricionales
    if (product.energy_kcal) totalEnergy += product.energy_kcal * proportion * 100;
    if (product.proteins_g) totalProteins += product.proteins_g * proportion * 100;
    if (product.total_fats_g) totalFats += product.total_fats_g * proportion * 100;
    if (product.saturated_fats_g) totalSaturatedFats += product.saturated_fats_g * proportion * 100;
    if (product.monounsaturated_fats_g) totalMonounsaturatedFats += product.monounsaturated_fats_g * proportion * 100;
    if (product.polyunsaturated_fats_g) totalPolyunsaturatedFats += product.polyunsaturated_fats_g * proportion * 100;
    if (product.cholesterol_mg) totalCholesterol += product.cholesterol_mg * proportion * 100;
    if (product.carbohydrates_g) totalCarbohydrates += product.carbohydrates_g * proportion * 100;
    if (product.total_sugars_g) totalSugars += product.total_sugars_g * proportion * 100;
    if (product.sodium_mg) totalSodium += product.sodium_mg * proportion * 100;
    if (product.dietary_fiber_g) totalFiber += product.dietary_fiber_g * proportion * 100;
  });
  
  return {
    calculated_energy_kcal: Math.round(totalEnergy * 100) / 100,
    calculated_proteins_g: Math.round(totalProteins * 100) / 100,
    calculated_total_fats_g: Math.round(totalFats * 100) / 100,
    calculated_saturated_fats_g: Math.round(totalSaturatedFats * 100) / 100,
    calculated_monounsaturated_fats_g: Math.round(totalMonounsaturatedFats * 100) / 100,
    calculated_polyunsaturated_fats_g: Math.round(totalPolyunsaturatedFats * 100) / 100,
    calculated_cholesterol_mg: Math.round(totalCholesterol * 100) / 100,
    calculated_carbohydrates_g: Math.round(totalCarbohydrates * 100) / 100,
    calculated_total_sugars_g: Math.round(totalSugars * 100) / 100,
    calculated_sodium_mg: Math.round(totalSodium * 100) / 100,
    calculated_dietary_fiber_g: Math.round(totalFiber * 100) / 100,
    has_nutritional_calculation: true,
    last_nutritional_calculation: new Date().toISOString(),
  };
};

/**
 * Listar recetas
 */
export const list = async (page = 1, filters = {}) => {
  await delay(200);
  let recipes = storage.get(STORAGE_KEYS.RECIPES, []);
  
  // Aplicar filtros
  if (filters.branch_id) {
    // Normalizar branch_id para comparación (convertir a string para comparación consistente)
    const branchIdStr = String(filters.branch_id);
    recipes = recipes.filter((r) => {
      const recipeBranch = r.branch?.id || r.branch;
      return String(recipeBranch) === branchIdStr;
    });
  }
  
  if (filters.recipe_type) {
    recipes = recipes.filter((r) => r.recipe_type === filters.recipe_type);
  }
  
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    recipes = recipes.filter((r) => r.name?.toLowerCase().includes(searchLower));
  }
  
  // Simular paginación
  const pageSize = 50;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const results = recipes.slice(start, end);
  
  return {
    results,
    count: recipes.length,
    page,
    page_size: pageSize,
  };
};

/**
 * Obtener una receta por ID
 */
export const retrieve = async (id) => {
  await delay(200);
  const recipes = storage.get(STORAGE_KEYS.RECIPES, []);
  const recipe = recipes.find((r) => r.id === id);
  
  if (!recipe) {
    throw new Error("Receta no encontrada");
  }
  
  // Agregar ingredientes
  const ingredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []).filter(
    (ing) => ing.recipe === id
  );
  recipe.ingredients = ingredients;
  
  return recipe;
};

/**
 * Crear una nueva receta
 */
export const create = async (data) => {
  await delay(300);
  const recipes = storage.get(STORAGE_KEYS.RECIPES, []);
  
  const newRecipe = {
    id: generateId(),
    ...data,
    created: new Date().toISOString(),
    modified: new Date().toISOString(),
    has_nutritional_calculation: false,
  };
  
  recipes.push(newRecipe);
  
  // Guardar y verificar que se guardó correctamente
  try {
    storage.set(STORAGE_KEYS.RECIPES, recipes);
    
    // Verificar que la receta se guardó correctamente
    const savedRecipes = storage.get(STORAGE_KEYS.RECIPES, []);
    const savedRecipe = savedRecipes.find((r) => r.id === newRecipe.id);
    
    if (!savedRecipe) {
      throw new Error("La receta no se guardó correctamente en localStorage");
    }
    
    return newRecipe;
  } catch (error) {
    console.error("Error al guardar receta:", error);
    throw error;
  }
};

/**
 * Actualizar una receta
 */
export const update = async (id, data) => {
  await delay(300);
  const recipes = storage.get(STORAGE_KEYS.RECIPES, []);
  const index = recipes.findIndex((r) => r.id === id);
  
  if (index === -1) {
    throw new Error("Receta no encontrada");
  }
  
  // Actualizar todos los campos que vienen en data (excepto undefined)
  // Esto permite actualizar strings vacíos y null si es necesario
  const updateData = {};
  Object.keys(data).forEach((key) => {
    const value = data[key];
    // Incluir todos los valores excepto undefined
    // Esto permite actualizar con strings vacíos, null, 0, etc.
    if (value !== undefined) {
      updateData[key] = value;
    }
  });
  
  // Guardar el estado anterior para el log
  const recetaAntes = { ...recipes[index] };
  
  recipes[index] = {
    ...recipes[index],
    ...updateData,
    modified: new Date().toISOString(),
  };
  
  console.log("Actualizando receta:", {
    id,
    datosRecibidos: data,
    datosActualizados: updateData,
    recetaAntes,
    recetaDespues: recipes[index],
  });
  
  // Guardar y verificar que se guardó correctamente
  try {
    storage.set(STORAGE_KEYS.RECIPES, recipes);
    
    // Verificar que la receta se actualizó correctamente
    const savedRecipes = storage.get(STORAGE_KEYS.RECIPES, []);
    const savedRecipe = savedRecipes.find((r) => r.id === id);
    
    if (!savedRecipe) {
      throw new Error("La receta no se actualizó correctamente en localStorage");
    }
    
    return recipes[index];
  } catch (error) {
    console.error("Error al actualizar receta:", error);
    throw error;
  }
};

/**
 * Eliminar una receta
 */
export const destroy = async (id) => {
  await delay(200);
  const recipes = storage.get(STORAGE_KEYS.RECIPES, []);
  const filtered = recipes.filter((r) => r.id !== id);
  
  // Eliminar ingredientes asociados
  const ingredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []);
  const filteredIngredients = ingredients.filter((ing) => ing.recipe !== id);
  
  // Guardar ingredientes filtrados y verificar
  try {
    storage.set(STORAGE_KEYS.RECIPE_INGREDIENTS, filteredIngredients);
    
    // Guardar recetas filtradas y verificar
    storage.set(STORAGE_KEYS.RECIPES, filtered);
    
    // Verificar que la receta se eliminó correctamente
    const savedRecipes = storage.get(STORAGE_KEYS.RECIPES, []);
    const deletedRecipe = savedRecipes.find((r) => r.id === id);
    
    if (deletedRecipe) {
      throw new Error("La receta no se eliminó correctamente de localStorage");
    }
    
    // Verificar que los ingredientes se eliminaron correctamente
    const savedIngredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []);
    const remainingIngredients = savedIngredients.filter((ing) => ing.recipe === id);
    
    if (remainingIngredients.length > 0) {
      console.warn(`Advertencia: Quedaron ${remainingIngredients.length} ingredientes huérfanos después de eliminar la receta`);
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error al eliminar receta:", error);
    throw error;
  }
};

/**
 * Calcular valores nutricionales de una receta
 */
export const calculateNutrition = async (id) => {
  await delay(500);
  const recipes = storage.get(STORAGE_KEYS.RECIPES, []);
  const index = recipes.findIndex((r) => r.id === id);
  
  if (index === -1) {
    throw new Error("Receta no encontrada");
  }
  
  const nutritionData = calculateNutritionFromIngredients(recipes[index]);
  recipes[index] = {
    ...recipes[index],
    ...nutritionData,
  };
  
  // Guardar y verificar que se guardó correctamente
  try {
    storage.set(STORAGE_KEYS.RECIPES, recipes);
    
    // Verificar que los valores nutricionales se guardaron correctamente
    const savedRecipes = storage.get(STORAGE_KEYS.RECIPES, []);
    const savedRecipe = savedRecipes.find((r) => r.id === id);
    
    if (!savedRecipe || !savedRecipe.has_nutritional_calculation) {
      throw new Error("Los valores nutricionales no se guardaron correctamente en localStorage");
    }
    
    return recipes[index];
  } catch (error) {
    console.error("Error al calcular y guardar nutrición:", error);
    throw error;
  }
};

/**
 * Obtener etiqueta nutricional formateada (MINSAL)
 */
export const getNutritionLabel = async (id) => {
  await delay(200);
  const recipe = await retrieve(id);
  
  if (!recipe.has_nutritional_calculation) {
    throw new Error("La receta no tiene valores nutricionales calculados");
  }
  
  return {
    recipe_name: recipe.name,
    recipe_code: recipe.code,
    total_yield_grams: recipe.total_yield_grams,
    values_per_100g: {
      energy_kcal: recipe.calculated_energy_kcal,
      proteins_g: recipe.calculated_proteins_g,
      total_fats_g: recipe.calculated_total_fats_g,
      saturated_fats_g: recipe.calculated_saturated_fats_g,
      monounsaturated_fats_g: recipe.calculated_monounsaturated_fats_g,
      polyunsaturated_fats_g: recipe.calculated_polyunsaturated_fats_g,
      cholesterol_mg: recipe.calculated_cholesterol_mg,
      carbohydrates_g: recipe.calculated_carbohydrates_g,
      total_sugars_g: recipe.calculated_total_sugars_g,
      sodium_mg: recipe.calculated_sodium_mg,
      dietary_fiber_g: recipe.calculated_dietary_fiber_g,
    },
    last_calculation: recipe.last_nutritional_calculation,
  };
};

/**
 * Descargar PDF de etiqueta nutricional
 * Genera un PDF real usando jsPDF
 */
export const downloadNutritionLabelPDF = async (id) => {
  await delay(500);
  const labelData = await getNutritionLabel(id);
  
  // Importar jsPDF
  const { jsPDF } = await import("jspdf");
  
  // Crear PDF (62mm x 100mm vertical según MINSAL)
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: [62, 100],
  });
  
  const margin = 3;
  let yPos = margin + 5;
  const lineHeight = 3.5;
  
  // Título
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");
  pdf.text("INFORMACIÓN NUTRICIONAL", 31, yPos, { align: "center" });
  yPos += lineHeight + 1;
  
  // Nombre del producto
  pdf.setFontSize(7);
  pdf.setFont("helvetica", "normal");
  const nameLines = pdf.splitTextToSize(labelData.recipe_name || "Producto", 56);
  pdf.text(nameLines, 31, yPos, { align: "center" });
  yPos += nameLines.length * lineHeight + 2;
  
  // Por 100g
  pdf.setFontSize(6);
  pdf.text("Por 100g de producto", 31, yPos, { align: "center" });
  yPos += lineHeight + 2;
  
  // Línea separadora
  pdf.setLineWidth(0.1);
  pdf.line(margin, yPos, 62 - margin, yPos);
  yPos += lineHeight;
  
  // Valores nutricionales
  pdf.setFontSize(6);
  const values = labelData.values_per_100g;
  
  const formatValue = (val) => {
    if (val === null || val === undefined) return "-";
    return parseFloat(val).toFixed(2);
  };
  
  const addLine = (label, value, indent = 0) => {
    pdf.setFont("helvetica", "normal");
    pdf.text(label, margin + indent, yPos);
    pdf.text(value, 62 - margin, yPos, { align: "right" });
    yPos += lineHeight;
  };
  
  addLine("Valor Energético", `${formatValue(values.energy_kcal)} kcal`);
  addLine("Proteínas", `${formatValue(values.proteins_g)} g`);
  addLine("Grasas Totales", `${formatValue(values.total_fats_g)} g`);
  addLine("  Grasas Saturadas", `${formatValue(values.saturated_fats_g)} g`, 3);
  addLine("  Grasas Monoinsaturadas", `${formatValue(values.monounsaturated_fats_g)} g`, 3);
  addLine("  Grasas Poliinsaturadas", `${formatValue(values.polyunsaturated_fats_g)} g`, 3);
  addLine("Colesterol", `${formatValue(values.cholesterol_mg)} mg`);
  addLine("Hidratos de Carbono", `${formatValue(values.carbohydrates_g)} g`);
  addLine("  Azúcares Totales", `${formatValue(values.total_sugars_g)} g`, 3);
  addLine("Sodio", `${formatValue(values.sodium_mg)} mg`);
  if (values.dietary_fiber_g) {
    addLine("Fibra Dietética", `${formatValue(values.dietary_fiber_g)} g`);
  }
  
  // Convertir a blob
  const pdfBlob = pdf.output("blob");
  return pdfBlob;
};

/**
 * Listar ingredientes nutricionales disponibles
 */
export const getNutritionalIngredients = async (branchId = null) => {
  await delay(200);
  const filters = {
    is_nutritional_ingredient: true,
  };
  
  if (branchId) {
    filters.branch_id = branchId;
  }
  
  const response = await import("./products").then((m) => m.list(1, filters));
  return response.results || [];
};

/**
 * Listar ingredientes de una receta
 */
export const listIngredients = async (recipeId) => {
  await delay(200);
  const ingredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []);
  return ingredients.filter((ing) => ing.recipe === recipeId);
};

/**
 * Crear un ingrediente para una receta
 */
export const createIngredient = async (data) => {
  await delay(300);
  const ingredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []);
  
  const newIngredient = {
    id: generateId(),
    ...data,
    created: new Date().toISOString(),
  };
  
  ingredients.push(newIngredient);
  
  // Guardar y verificar que se guardó correctamente
  try {
    storage.set(STORAGE_KEYS.RECIPE_INGREDIENTS, ingredients);
    
    // Verificar que el ingrediente se guardó correctamente
    const savedIngredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []);
    const savedIngredient = savedIngredients.find((ing) => ing.id === newIngredient.id);
    
    if (!savedIngredient) {
      throw new Error("El ingrediente no se guardó correctamente en localStorage");
    }
    
    return newIngredient;
  } catch (error) {
    console.error("Error al guardar ingrediente:", error);
    throw error;
  }
};

/**
 * Actualizar un ingrediente de receta
 */
export const updateIngredient = async (id, data) => {
  await delay(300);
  const ingredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []);
  const index = ingredients.findIndex((ing) => ing.id === id);
  
  if (index === -1) {
    throw new Error("Ingrediente no encontrado");
  }
  
  ingredients[index] = {
    ...ingredients[index],
    ...data,
  };
  
  // Guardar y verificar que se guardó correctamente
  try {
    storage.set(STORAGE_KEYS.RECIPE_INGREDIENTS, ingredients);
    
    // Verificar que el ingrediente se actualizó correctamente
    const savedIngredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []);
    const savedIngredient = savedIngredients.find((ing) => ing.id === id);
    
    if (!savedIngredient) {
      throw new Error("El ingrediente no se actualizó correctamente en localStorage");
    }
    
    return ingredients[index];
  } catch (error) {
    console.error("Error al actualizar ingrediente:", error);
    throw error;
  }
};

/**
 * Eliminar un ingrediente de receta
 */
export const deleteIngredient = async (id) => {
  await delay(200);
  const ingredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []);
  const filtered = ingredients.filter((ing) => ing.id !== id);
  
  // Guardar y verificar que se eliminó correctamente
  try {
    storage.set(STORAGE_KEYS.RECIPE_INGREDIENTS, filtered);
    
    // Verificar que el ingrediente se eliminó correctamente
    const savedIngredients = storage.get(STORAGE_KEYS.RECIPE_INGREDIENTS, []);
    const deletedIngredient = savedIngredients.find((ing) => ing.id === id);
    
    if (deletedIngredient) {
      throw new Error("El ingrediente no se eliminó correctamente de localStorage");
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error al eliminar ingrediente:", error);
    throw error;
  }
};
